import { FulfillmentMethod } from "@aitisal/contracts/common";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createSafeJSONStorage } from "./safeStorage";
import { cartStore } from "@aitisal/ui";

interface User {
  userPhone: string;
  userAddress: string;
  userNotes: string;
}

interface SessionState {
  userName: string;
  restaurantId: string;
  brandId: string;
  restaurantAreaId: string;
  selectedAreaId: string | null;
  insideDeliveryArea: boolean;
  distanceFromRestaurant: any;
  pointOfOrder: Record<string, any>;
  user: User;
  orderId: string;
  orderIds: string[];
  metadata: Record<string, any>;
  isOrderingEnabled: boolean;
  signedInUserDetails: Record<string, any>;
  latitude: number;
  longitude: number;
  personAddress: string;
  savedAddresses: Record<string, any>[];
  isPickup: boolean;
  fullfillmentType: string;
  selectedDeliveryOption: any;
  locationTimestamp: number | null;
  setSelectedAreaId: (id: string | null) => void;
  setUserName: (name: string) => void;
  setSelectedDeliveryOption: (option: any) => void;
  setFullfillmentType: (fullfillmentType: FulfillmentMethod) => void;
  setLatitude: (latitude: number) => void;
  setLongitude: (longitude: number) => void;
  setPersonAddress: (address: string) => void;
  setSavedAddresses: (addresses: Record<string, any>[]) => void;
  setRestaurantId: (id: string) => void;
  setPointOfOrder: (po: Record<string, any>) => void;
  setOrderId: (id: string) => void;
  setOrderIds: (ids: string[]) => void;
  addOrderId: (id: string) => void;
  resetOrderIds: () => void;
  setUser: (userPhone: string, userAddress: string, userNotes: string) => void;
  setMetadata: (data: Record<string, any>) => void;
  setSignedInUserDetails: (userDetails: Record<string, any>) => void;
  resetUserSession: () => void;
  resetRestaurant: () => void;
  resetLocation: () => void;
  setRestaurantAreaId: (id: string) => void;
  setInsideDeliveryArea: (insideDeliveryArea: boolean) => void;
  setDistanceFromRestaurant: (distanceFromRestaurant: number) => void;
  setIsOrderingEnabled: (active: boolean) => void;
  setIsPickup: (isPickup: boolean) => void;
  setBrandId: (id: string) => void;
}

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      restaurantId: "",
      restaurantAreaId: "",
      selectedAreaId: null,
      insideDeliveryArea: false,
      distanceFromRestaurant: 0,
      userName: "",
      pointOfOrder: {},
      user: {
        userPhone: "",
        userAddress: "",
        userNotes: "",
      },
      brandId: "",
      orderId: "",
      orderIds: [],
      metadata: {},
      isUserLoggedIn: false,
      isOrderingEnabled: false,
      signedInUserDetails: {},
      latitude: 0,
      longitude: 0,
      personAddress: "",
      savedAddresses: [],
      isPickup: false,
      fullfillmentType: FulfillmentMethod.BRANCH_DELIVERY,
      selectedDeliveryOption: null,
      locationTimestamp: null,
      setSelectedAreaId: (id) => {
        set({ selectedAreaId: id, locationTimestamp: Date.now() });
      },
      setSelectedDeliveryOption: (option: any) => {
        set({ selectedDeliveryOption: option });
      },
      setUserName: (name: string) => {
        set({ userName: name });
      },
      setBrandId: (id: string) => {
        set({ brandId: id });
      },
      setIsPickup: (isPickup: boolean) => {
        set({ isPickup });
      },
      setFullfillmentType: (fullfillmentType: string) => {
        set({ fullfillmentType: fullfillmentType });
      },
      setLatitude: (latitude) => {
        set({ latitude, locationTimestamp: Date.now() });
      },
      setLongitude: (longitude) => {
        set({ longitude, locationTimestamp: Date.now() });
      },
      setPersonAddress: (address) => {
        set({ personAddress: address, locationTimestamp: Date.now() });
      },
      setSavedAddresses: (addresses) => {
        set({ savedAddresses: addresses });
      },
      setRestaurantId: (id) => {
        set({ restaurantId: id, locationTimestamp: Date.now() });
      },
      setRestaurantAreaId: (id) => {
        set({ restaurantAreaId: id, locationTimestamp: Date.now() });
      },
      setInsideDeliveryArea: (insideDeliveryArea: boolean) => {
        set({ insideDeliveryArea: insideDeliveryArea });
      },
      setDistanceFromRestaurant: (distanceFromRestaurant: number) => {
        set({ distanceFromRestaurant: distanceFromRestaurant });
      },
      setPointOfOrder: (po) => {
        set({ pointOfOrder: po });
      },
      setOrderId: (id) => {
        set({ orderId: id });
      },
      setOrderIds: (ids) => {
        set({ orderIds: ids });
      },
      addOrderId: (id) => {
        set((state) => ({ orderIds: [...state.orderIds, id] }));
      },
      resetOrderIds: () => {
        set({ orderIds: [] });
      },
      setUser: (userPhone, userAddress, userNotes) => {
        set({ user: { userPhone, userAddress, userNotes } });
      },
      setMetadata: (data) => {
        set({ metadata: data });
      },
      setSignedInUserDetails: (userDetails) => {
        set({ signedInUserDetails: userDetails });
      },
      resetUserSession: () => {},
      resetRestaurant: () => {
        localStorage.removeItem("session-store");
        set({ restaurantId: "", restaurantAreaId: "", selectedAreaId: null });
      },
      resetLocation: () => {
        set({
          restaurantId: "",
          restaurantAreaId: "",
          selectedAreaId: null,
          latitude: 0,
          longitude: 0,
          personAddress: "",
          insideDeliveryArea: false,
          distanceFromRestaurant: 0,
          locationTimestamp: null,
        });
      },
      setIsOrderingEnabled: (active) => {
        set({ isOrderingEnabled: active });
      },
    }),
    {
      name: "session-store",
      storage: createSafeJSONStorage(() => localStorage),
      onRehydrateStorage: () => {
        return (rehydratedState, error) => {
          if (error) {
            console.error("An error occurred during rehydration", error);
            return;
          }
          if (rehydratedState && rehydratedState.locationTimestamp) {
            const now = Date.now();
            const twoHours = 2 * 60 * 60 * 1000;
            if (now - rehydratedState.locationTimestamp > twoHours) {
              rehydratedState.resetLocation();
              cartStore.getState().resetCart();
            }
          }
        };
      },
    },
  ),
);
